import React, { useRef, useState, useMemo, useEffect } from "react";
import { useIntl } from 'react-intl';
import classNames from "classnames";
import { countries, getCountryCode } from "countries-list";
import ReactCountryFlag from "react-country-flag";
import Popout from "components/Popout";
import TextInput from "components/TextInput";

const allCountries = Object.values(countries);

const CountryCodeSelector = ({
  selectedCountry,
  onSelect,
  disabled = false,
  id,
  dataCy
}) => {
  const intl = useIntl();
  const anchorRef = useRef(null);
  const selectedRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    if (isOpen && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: "start" });
    }
  }, [isOpen]);

  const filteredCountries = useMemo(() => {
    return allCountries.filter((country) => {
      const search = searchValue.toLowerCase();
      return (
        country.name.toLowerCase().includes(search) ||
        country.native?.toLowerCase().includes(search)
      );
    });
  }, [searchValue]);

  const handleSelect = (country) => {
    onSelect(country);
    setIsOpen(false);
    setSearchValue("");
  };

  return (
    <>
      <button
        ref={anchorRef}
        id={id}
        data-cy={dataCy}
        type="button"
        onClick={(e) => {e.preventDefault(); !disabled && setIsOpen(!isOpen)}}
        className={classNames(
          "h-full px-2 flex items-center justify-center gap-1 border border-[var(--color-gray-100)] rounded-lg",
          !disabled && "hover:bg-[var(--color-gray-50)]"
        )}
      >
        <ReactCountryFlag
          svg
          countryCode={getCountryCode(selectedCountry.name)}
        />
        <span className="tw-paragraph-sm font-medium">
          +{selectedCountry.phone[0]}
        </span>
      </button>

      <Popout
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        anchorRef={anchorRef}
        positioning="bottom-right"
        title={intl.formatMessage({ id: "comp.table.header.countryCode" })}
      >
        <div className="w-[330px] min-h-[369px] space-y-4">
          <TextInput
            id="country-code-selector-text-input"
            data-cy="country-code-selector-text-input"
            placeholder={intl.formatMessage({ id: "comp.header.select.countryCode" })}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />

          <div className="max-h-[300px] overflow-auto custom-scrollbar space-y-1 mr-1">
            {filteredCountries.map((country, idx) => (
              <button
                id={`country-code-selector-${country.name}-${idx}`}
                data-cy={`country-code-selector-${country.name}-${idx}`}
                key={country.name + idx}
                ref={country.name === selectedCountry.name ? selectedRef : null}
                onClick={(e) => {e.preventDefault(); handleSelect(country)}}
                className={classNames(
                  "flex justify-between items-center w-full px-3 py-2 rounded-lg hover:bg-[var(--color-gray-100)]",
                  country.name === selectedCountry.name && "bg-[var(--color-gray-100)] font-semibold"
                )}
              >
                <span className="flex items-center gap-2 tw-paragraph-sm">
                  <ReactCountryFlag
                    svg
                    countryCode={getCountryCode(country.name)}
                  />
                  {country.native === "Éire" ? country.name : country.native}
                </span>
                <span className="tw-paragraph-sm font-medium text-[var(--color-gray-500)]">
                  +{country.phone[0]}
                </span>
              </button>
            ))}
          </div>
        </div>
      </Popout>
    </>
  );
};

export default CountryCodeSelector;